import { useCallback, useEffect, useState } from 'react';
import {
  useContentDispatch,
  useContentSelector,
  ComponentTypesActions,
  ContentEntityTypeActions,
  ComponentsActions,
  getContentEntityListsLookups,
} from '@wajeez-web/stores/content';
import { Server } from '@wajeez/wajeez.microservices';
import { Spin } from '@wajeez-web/components/ui';

import { InputFieldWithSelectorOptions } from './ComponentFormBuilder';
import { ComponentCreateForm } from './ComponentCreateForm';
import { ComponentEditForm } from './ComponentEditForm';
import { useGetComponentByIdMultilingual } from '@wajeez-web/components/helpers';

type SelectorOption = {
  value: string | undefined;
  label: string | undefined;
};

type SelectorOptionsGrouped = {
  label: string | undefined;
  options: SelectorOption[];
};

export interface InputField {
  name: string | undefined;
  order: number | undefined;
  label: string | undefined;
  placeHolder: string | undefined;
  required: boolean | undefined;
  fieldType: Server.FieldType | undefined;
  fieldOptions: Server.ComponentTypeFieldOptionsOutput | undefined;
  permission: string | undefined;
}

interface ComponentFormProps {
  type: 'Create' | 'Edit';
  onSubmit: () => void;
  onItemTemplateCloneSaveAndEdit: (itemTemplateId: string) => void;
  onItemTemplateEditButtonClick: (itemTemplateId: string) => void;
  componentTypeId?: string | undefined;
  onCancel?: () => void;
}

export const ComponentForm = ({
  type,
  onSubmit,
  onItemTemplateCloneSaveAndEdit,
  onItemTemplateEditButtonClick,
  componentTypeId,
  onCancel,
}: ComponentFormProps) => {
  const contentDispatch = useContentDispatch();

  const [inputFields, setInputFields] = useState<InputField[] | undefined>();
  const [inputFieldsWithSelectorOptions, setInputFieldsWithSelectorOptions] =
    useState<InputFieldWithSelectorOptions[] | undefined>(undefined);
  const [
    isGetComponentLookupsByComponentTypesIdsSuccess,
    setIsGetComponentLookupsByComponentTypesIdsSuccess,
  ] = useState(false);

  const componentType = useContentSelector(
    ({ componentTypes }) => componentTypes.componentTypeById
  );
  const componentTypeData = componentType.data;

  const componentLookupsByComponentTypeIds = useContentSelector(
    ({ components }) => components.componentLookupsByComponentTypeId
  );

  const contentEntityTypesLookups = useContentSelector(
    ({ contentEntityType }) => contentEntityType.lookups
  );
  const selectSortFactors = useContentSelector(
    ({ contentEntityType }) => contentEntityType.selectSortFactors
  );

  const { status: componentDetailsMultilingualStatus } =
    useGetComponentByIdMultilingual();

  const getComponentLookupsByComponentTypesIds = useCallback(async () => {
    if (inputFields) {
      const status = [];

      for (let inputField of inputFields) {
        if (inputField.fieldType === Server.FieldType.RequiredComponent) {
          const typeId = inputField.fieldOptions?.typeId;

          if (typeId && !componentLookupsByComponentTypeIds.typeIds[typeId]) {
            const res = await contentDispatch(
              ComponentsActions.getComponentLookupsByComponentTypeIdsAsync({
                componentTypeIds: [typeId],
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              status.push('fulfilled');
            } else {
              status.push('rejected');
            }
          }
        }
      }

      const isAllFulfilled = !status.find((s) => s === 'rejected');

      if (isAllFulfilled || (status.length === 0 && inputFields.length === 0)) {
        setIsGetComponentLookupsByComponentTypesIdsSuccess(true);
      }
    }
  }, [inputFields?.length, contentDispatch]);

  useEffect(() => {
    componentTypeData &&
      setInputFields(
        componentTypeData.fields?.map((f) => {
          return {
            name: f.name,
            order: f.order,
            label: f.label,
            placeHolder: f.placeHolder,
            required: f.required,
            fieldType: f.fieldType,
            fieldOptions: f.fieldOptions,
            permission: f.permission,
          };
        })
      );

    return () => {
      setInputFields(undefined);
    };
  }, [componentTypeData?.fields]);

  useEffect(() => {
    const allowedItemTemplateTypes =
      componentTypeData?.allowedItemTemplateTypes;

    if (
      componentTypeData &&
      componentTypeData.contentItemType === Server.ContentItemType.Collection &&
      allowedItemTemplateTypes &&
      !componentLookupsByComponentTypeIds.typeIds[
        JSON.stringify(allowedItemTemplateTypes)
      ]
    ) {
      contentDispatch(
        ComponentsActions.getComponentLookupsByComponentTypeIdsAsync({
          componentTypeIds: allowedItemTemplateTypes,
        })
      );
    }
  }, [componentTypeData, componentLookupsByComponentTypeIds]);

  useEffect(() => {
    getComponentLookupsByComponentTypesIds();
  }, [getComponentLookupsByComponentTypesIds]);

  useEffect(() => {
    if (
      inputFields &&
      contentEntityTypesLookups.length &&
      isGetComponentLookupsByComponentTypesIdsSuccess
    ) {
      const newInputFields = inputFields.map((i) => {
        return {
          ...i,
          mappedFieldOptionsGrouped: [] as SelectorOptionsGrouped[],
          componentTypesOptions: [] as SelectorOption[],
          allowedPredefinedComponentsOptions: [] as SelectorOption[],
        };
      });

      for (let newInputField of newInputFields) {
        if (newInputField.fieldType === Server.FieldType.MappedField) {
          newInputField.mappedFieldOptionsGrouped =
            contentEntityTypesLookups.map((t) => {
              return {
                label: t.displayNamePlural + ` (${t.name})`,
                options:
                  t.fields?.map((f) => {
                    return {
                      value: f.id?.toString(),
                      label: f.label,
                    };
                  }) || [],
              };
            });
        } else if (
          newInputField.fieldType === Server.FieldType.RequiredComponent
        ) {
          newInputField.componentTypesOptions =
            componentLookupsByComponentTypeIds.typeIds[
              JSON.stringify([newInputField.fieldOptions?.typeId])
            ]?.data.map((d) => {
              return {
                value: d.id,
                label: d.referenceName,
              };
            });
        } else if (
          newInputField.fieldType === Server.FieldType.PredefinedComponent
        ) {
          newInputField.allowedPredefinedComponentsOptions =
            newInputField.fieldOptions?.allowedPredefinedComponents?.map(
              (p) => {
                return {
                  value: p,
                  label: p,
                };
              }
            ) || [];
        }
      }

      setInputFieldsWithSelectorOptions(newInputFields);
    }

    return () => {
      setInputFieldsWithSelectorOptions(undefined);
    };
  }, [
    contentEntityTypesLookups.length,
    inputFields?.length,
    componentLookupsByComponentTypeIds,
    isGetComponentLookupsByComponentTypesIdsSuccess,
    contentDispatch,
  ]);

  useEffect(() => {
    componentTypeId &&
      contentDispatch(
        ComponentTypesActions.getComponentTypeByIdAsync(
          parseInt(componentTypeId)
        )
      );

    return () => {
      contentDispatch(ComponentTypesActions.clearComponentTypeById());
    };
  }, [componentTypeId, contentDispatch]);

  useEffect(() => {
    contentDispatch(
      ContentEntityTypeActions.getContentEntityTypeLookupsAsync()
    );

    contentDispatch(ContentEntityTypeActions.getSelectSortFactorsAsync());

    getContentEntityListsLookups({});
  }, [contentDispatch]);

  return (
    <div className="w-[800px] mx-auto px-4 py-6">
      {componentType.status === 'loading' ||
      !componentTypeData ||
      componentLookupsByComponentTypeIds.status === 'loading' ||
      componentDetailsMultilingualStatus === 'loading' ||
      selectSortFactors.status === 'loading' ||
      !selectSortFactors.data ? (
        <Spin />
      ) : (
        <div className="w-full">
          <div className="w-full pb-6 border-b-[2px]">
            <h1 className="text-[26px] font-bold mb-1">
              {componentTypeData.title}
              &nbsp;
              <span className="text-[14px] font-normal">
                ({componentTypeData.name})
              </span>
            </h1>
            <h2 className="text-[17px] font-normal">
              {componentTypeData.description}
            </h2>
          </div>
          <div className="mt-6">
            {inputFields &&
              contentEntityTypesLookups.length &&
              isGetComponentLookupsByComponentTypesIdsSuccess &&
              componentTypeData &&
              inputFieldsWithSelectorOptions &&
              (type === 'Create' ? (
                <ComponentCreateForm
                  componentTypeId={Number(componentTypeId)}
                  inputFieldsWithSelectorOptions={
                    inputFieldsWithSelectorOptions
                  }
                  onSubmit={onSubmit}
                  onItemTemplateCloneSaveAndEdit={
                    onItemTemplateCloneSaveAndEdit
                  }
                  onItemTemplateEditButtonClick={onItemTemplateEditButtonClick}
                  onCancel={onCancel}
                />
              ) : (
                <ComponentEditForm
                  inputFieldsWithSelectorOptions={
                    inputFieldsWithSelectorOptions
                  }
                  onSubmit={onSubmit}
                  onItemTemplateCloneSaveAndEdit={
                    onItemTemplateCloneSaveAndEdit
                  }
                  onItemTemplateEditButtonClick={onItemTemplateEditButtonClick}
                  onCancel={onCancel}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
